import { useRef, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { useHttp } from "../hooks/useHttp";
import { logger } from "../utils/logger";

const carouselCards = [
  {
    id: 1,
    image: "/slider/catgame-claim/1.png",
    title: "Tap the chip",
    description:
      "Hold your phone near the CatGame tag. A page will open, click <span class='font-medium text-black'>Claim now</span>.",
  },
  {
    id: 2,
    image: "/slider/catgame-claim/2.png",
    title: "Enter your email",
    description:
      "Enter your email address to claim your digital collectible certificate.",
  },
  {
    id: 3,
    image: "/slider/catgame-claim/3.png",
    title: "Check your inbox",
    description:
      "Your certificate will be sent to your email. Check your inbox for your CatGame authenticity certificate!",
  },
];

// Drawer types for the flow
type DrawerType =
  | "loading"
  | "scan_chip"
  | "claim_form"
  | "claiming"
  | "success"
  | "already_claimed"
  | "error"
  | null;

// API Response interface
interface CatgameClaimResponse {
  success: boolean;
  action: "ready_to_claim" | "already_claimed" | "claimed";
  uid?: string;
  toyType?: number;
  message: string;
}

export const ClaimCatGame = () => {
  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sendRequest } = useHttp();
  const { sendRequest: sendSessionRequest } = useHttp(); // Separate instance for session check

  const uid = searchParams.get("uid");

  // State management
  const [currentDrawer, setCurrentDrawer] = useState<DrawerType>(null);
  const [catgameResponse, setCatgameResponse] =
    useState<CatgameClaimResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const settings = {
    dots: false,
    infinite: false,
    speed: 450,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    arrows: false,
    afterChange: (current: number) => {
      setCurrentSlide(current);
    },
  };

  // Validate UID and call backend API
  useEffect(() => {
    let isMounted = true;

    const validateUID = async () => {
      // No UID - show landing page
      if (!uid) {
        logger.ui("CatGame claim page loaded without UID parameter");
        return;
      }

      // Validate UID format
      if (!/^[A-F0-9]+$/i.test(uid)) {
        if (isMounted) {
          setError("Invalid UID format");
          setCurrentDrawer("error");
        }
        return;
      }

      // Call backend API to check UID status
      if (!isMounted) return;
      setCurrentDrawer("loading");
      setError(null);

      try {
        logger.api("Calling /catgame_claim API", { uid });

        const { data } = await sendRequest<CatgameClaimResponse>({
          endpoint: `/catgame/claim?uid=${encodeURIComponent(uid)}`,
          method: "GET",
          responseType: "json",
        });

        if (!isMounted) return;
        logger.api("CatGame claim API response received", { response: data });
        setCatgameResponse(data);

        if (!data.success) {
          setError(data.message);
          if (data.action === "already_claimed") {
            setCurrentDrawer("already_claimed");
          } else {
            setCurrentDrawer("error");
          }
          return;
        }

        // Ready to claim - show claim form
        if (data.action === "ready_to_claim") {
          setCurrentDrawer("claim_form");
        }
      } catch (err: any) {
        if (!isMounted) return;
        if (err?.name === "AbortError" || err?.message?.includes("abort")) {
          logger.ui(
            "Request aborted (component unmounted or new request started)"
          );
          return;
        }
        logger.apiError("CatGame claim API call error", err as Error);
        setError(
          err instanceof Error ? err.message : "Failed to validate UID"
        );
        setCurrentDrawer("error");
      }
    };

    validateUID();

    return () => {
      isMounted = false;
    };
  }, [uid, sendRequest]);

  // Check session on mount and prefill email if logged in
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data } = await sendSessionRequest<{
          authenticated: boolean;
          email?: string;
        }>({
          endpoint: '/catgame/session',
          method: 'GET',
          responseType: 'json',
        });

        if (data.authenticated && data.email) {
          setEmail(data.email);
        }
      } catch (err) {
        // Silently fail - no session
      }
    };

    checkSession();
  }, [sendSessionRequest]);

  useEffect(() => {
    if (currentDrawer) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [currentDrawer]);

  // Handle email submission and claim
  const handleEmailSubmit = async () => {
    if (!email || !uid) {
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setCurrentDrawer("claiming");
    setError(null);

    try {
      logger.api("Claiming CatGame", { uid, email });

      const { data } = await sendRequest<CatgameClaimResponse>({
        endpoint: `/catgame/claim?uid=${encodeURIComponent(uid)}`,
        method: "POST",
        payload: { email },
        responseType: "json",
      });

      logger.api("Claim response received", { response: data });

      if (data.success && data.action === "claimed") {
        setCatgameResponse((prev) => prev ? { ...prev, toyType: data.toyType } : prev);
        setCurrentDrawer("success");
      } else {
        setError(data.message || "Failed to claim");
        setCurrentDrawer("claim_form");
      }
    } catch (err: any) {
      logger.apiError("Failed to claim", err as Error);
      setError(
        err instanceof Error ? err.message : "Failed to claim chip"
      );
      setCurrentDrawer("claim_form");
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <div className="py-5">
      <div className="px-6 mb-8">
        {uid && (
          <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mb-4">
            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 animate-pulse" />

            <img
              src={currentDrawer === "success" && catgameResponse?.toyType
                ? `/images/catgame/toys/${catgameResponse.toyType}.png`
                : "/images/catgame/catgame-landing.png"}
              alt="CatGame"
              className="object-cover absolute inset-0 w-full h-full"
            />

            <img
              src="/images/223-logo.png"
              alt="223-logo"
              className="absolute top-4 left-4 max-w-[83px]"
            />

            <img
              src="/svg/catgame/catgame-icon.svg"
              alt="catgame-logo"
              className="absolute top-4 right-4 max-w-[50px]"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />

            <div className="backdrop-blur-3xl rounded-[19px] py-4 absolute bottom-3 right-3 left-3 bg-white/10">
              <div className="max-w-[240px] mx-auto flex flex-col items-center justify-center gap-2">
                <h3 className="text-lg font-bold text-center">
                  223 x CatGame Authenticity Certificate
                </h3>
              </div>
            </div>
          </div>
        )}

        {!uid && (
          <div className="py-[22px] rounded-[12px] bg-gradient-to-r from-[#3FA3FF] to-[#FF5A7F] flex flex-col items-center justify-center gap-2">
            <div className="font-bold text-center text-white">
              Sign up for 223 and Claim <br /> CatGame Collectibles in a Flash
            </div>

            <button
              onClick={() => setCurrentDrawer("scan_chip")}
              className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 hover:bg-[#4a4a4a] transition-colors cursor-pointer"
            >
              Claim Now
            </button>
          </div>
        )}
      </div>

      <div className="px-6">
        <h3 className="mb-4 text-2xl font-bold text-center">How it Works</h3>

        <div className="relative carousel-container">
          <Slider ref={sliderRef} {...settings}>
            {carouselCards.map((card) => (
              <CarouselCard key={card.id} card={card} />
            ))}
          </Slider>
        </div>

        <div className="my-3 text-center">
          <h4 className="mb-2 text-xl font-bold">
            {carouselCards[currentSlide].title}
          </h4>

          <p
            className="text-lg leading-tight text-[#626262] whitespace-pre-line"
            dangerouslySetInnerHTML={{
              __html: carouselCards[currentSlide].description,
            }}
          ></p>
        </div>

        <div className="flex gap-2 justify-center items-center mt-5 mb-6">
          {carouselCards.map((_, index) => (
            <div
              key={index}
              className={`rounded-full cursor-pointer transition-all ${currentSlide === index
                ? "w-[37px] h-[10px] bg-[#000000]"
                : "w-[10px] h-[10px] bg-[#CCCCCC]"
                }`}
              onClick={() => sliderRef.current?.slickGoTo(index)}
            />
          ))}
        </div>
      </div>

      {/* Loading Drawer */}
      {currentDrawer === "loading" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full animate-spin">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>

            <div className="text-center space-y-2.5 mt-8">
              <div className="text-2xl font-bold text-black">
                Validating UID
              </div>
              <div className="text-[#626262]">
                Please wait while we verify your CatGame...
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scan Chip Drawer */}
      {currentDrawer === "scan_chip" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img src="/svg/scan-chip.svg" alt="scan-chip" />

            <div className="mt-8 text-center">
              <div className="text-2xl font-bold text-black">
                Please scan the NFC chip
              </div>
              <div className="text-[#626262] mt-2">
                Tap your phone near the CatGame tag to continue
              </div>
            </div>

            <button
              onClick={() => setCurrentDrawer(null)}
              className="mt-6 py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Claim Form Drawer */}
      {currentDrawer === "claim_form" && catgameResponse && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            <div className="space-y-2.5">
              <div className="text-2xl font-bold text-black">
                Claim with your email
              </div>

              <div className="text-[#626262]">
                Please enter your email address to claim your reward.
              </div>

              <div className="!mt-6 relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@gmail.com"
                  className="w-full pl-[46px] pr-4 border border-[#B4B4B4] focus:outline-none h-11 py-2.5 text-base"
                />

                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 -bottom-0.5">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M20.34 6.8206L12.7644 13.0942C12.5492 13.2726 12.2784 13.3702 11.9988 13.3702C11.7193 13.3702 11.4485 13.2726 11.2332 13.0942L3.66122 6.8206C3.6206 6.9429 3.59993 7.07094 3.60002 7.1998V16.7998C3.60002 17.1181 3.72645 17.4233 3.9515 17.6483C4.17654 17.8734 4.48176 17.9998 4.80002 17.9998H19.2C19.5183 17.9998 19.8235 17.8734 20.0486 17.6483C20.2736 17.4233 20.4 17.1181 20.4 16.7998V7.1998C20.4005 7.071 20.3802 6.94297 20.34 6.8206ZM4.80002 4.7998H19.2C19.8365 4.7998 20.447 5.05266 20.8971 5.50275C21.3472 5.95284 21.6 6.56328 21.6 7.1998V16.7998C21.6 17.4363 21.3472 18.0468 20.8971 18.4969C20.447 18.9469 19.8365 19.1998 19.2 19.1998H4.80002C4.1635 19.1998 3.55306 18.9469 3.10297 18.4969C2.65288 18.0468 2.40002 17.4363 2.40002 16.7998V7.1998C2.40002 6.56328 2.65288 5.95284 3.10297 5.50275C3.55306 5.05266 4.1635 4.7998 4.80002 4.7998ZM4.54802 5.9998L11.2392 11.5234C11.4535 11.7004 11.7226 11.7975 12.0006 11.7982C12.2785 11.7988 12.5481 11.703 12.7632 11.527L19.5216 5.9998H4.54802Z" fill="black" />
                  </svg>
                </div>
              </div>

              {error && (
                <div className="px-4 py-3 text-sm text-red-700 bg-red-100 rounded border border-red-400">
                  {error}
                </div>
              )}

              <button
                onClick={handleEmailSubmit}
                disabled={!email || isSubmitting}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed !mt-6"
              >
                {isSubmitting ? "Claiming..." : "Claim Now"}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Claiming (Processing) Drawer */}
      {currentDrawer === "claiming" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img src="/images/catgame/claiming-catgame.png" alt="claiming" />

            <div className="text-center space-y-2.5 mt-8">
              <div className="text-2xl font-bold text-black">Claiming</div>

              <div className="text-[#626262]">
                Claiming your Digital Collectible. Please wait.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Drawer */}
      {currentDrawer === "success" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] overflow-hidden p-6 pb-10">
            <div className="text-center">
              <div className="text-xl font-bold">🎉 CONGRATS!</div>

              <div className="text-sm text-[#626262] mt-2">
                You should receive your certificate via email shortly. You can
                also view your certificate{" "}
                <span className="text-black underline cursor-pointer">
                  here
                </span>
              </div>
            </div>

            <div className="mt-4">
              <img
                src="/svg/catgame/catgame-claimed.svg"
                alt="success"
                className="w-full"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>

            <div>
              <div className="text-center mt-4 mb-5">
                <div className="font-bold text-[22px]">CatGame</div>
                <div className="text-lg text-[#626262]">MinoCats</div>
              </div>

              <button
                onClick={() => navigate("/rewards/catgame")}
                className="w-full h-10 font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800"
              >
                Go to Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Already Claimed Drawer */}
      {currentDrawer === "already_claimed" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            <div className="text-center">
              <img
                src="/svg/catgame/catgame-error.svg"
                alt="already-claimed"
                className="mx-auto mb-4 w-32 h-32"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />

              <h2 className="mb-2 text-xl font-bold">Already Claimed</h2>

              <p className="mb-6 text-gray-600">
                {error || "This CatGame collectible has already been claimed"}
              </p>

              <button
                onClick={() => navigate("/rewards/catgame")}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800"
              >
                Go to profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Drawer */}
      {currentDrawer === "error" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img
              src="/svg/catgame/catgame-error.svg"
              alt="error"
              className="mb-4 w-32 h-32"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />

            <div className="text-center space-y-2.5">
              <div className="text-2xl font-bold text-black">
                Something Went Wrong
              </div>

              <div className="text-[#626262]">
                {error || "An unexpected error occurred. Please try again."}
              </div>

              <button
                onClick={() =>
                  window.open("/support", "_blank", "noopener,noreferrer")
                }
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-6"
              >
                Contact Support
              </button>

              <div className="flex items-center justify-center gap-1 text-sm text-gray-500 !mt-4">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>

                <span>
                  Please contact support if you believe this is an error.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CarouselCard = ({
  card,
}: {
  card: { id: number; image: string; title: string; description: string };
}) => {
  return (
    <div className="w-full rounded-[14px] overflow-hidden bg-gradient-to-br from-purple-400 via-purple-500 to-purple-600 shadow-xl mx-auto">
      <div className="relative w-full h-full rounded-[14px] overflow-hidden aspect-[3/4]">
        <img
          src={card.image}
          alt={card.title}
          className="object-cover absolute inset-0 w-full h-full"
        />
      </div>
    </div>
  );
};
